import React, { useState } from "react"
import "./LandingPage.css"

const SECTIONS = [
  { id: "getting-started", label: "Getting Started" },
  { id: "dashboard", label: "Dashboard" },
  { id: "agents", label: "Agents" },
  { id: "channels", label: "Channel Integrations" },
  { id: "skills", label: "Knowledge & Skills" },
  { id: "collaboration", label: "Multi-Agent Collaboration" },
  { id: "credentials", label: "Credential Vault" },
  { id: "governance", label: "Governance & Approvals" },
  { id: "api-keys", label: "API Keys & Providers" },
  { id: "observability", label: "Observability" },
  { id: "administration", label: "Administration" },
]

function GuideImage({ src, alt }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="ug-screenshot" onClick={() => setExpanded(!expanded)}>
      <img src={`/images/guide/${src}`} alt={alt} loading="lazy" />
      <span className="ug-screenshot-hint">{expanded ? "Click to collapse" : "Click to expand"}</span>
    </div>
  )
}

function Step({ n, title, children }) {
  return (
    <div className="ug-step">
      <div className="ug-step-num">{n}</div>
      <div className="ug-step-body">
        <h4>{title}</h4>
        {children}
      </div>
    </div>
  )
}

export default function UserGuide() {
  const [activeSection, setActiveSection] = useState("getting-started")

  const scrollTo = (id) => {
    setActiveSection(id)
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <div className="lp2-shell">
      <header className="lp2-header">
        <nav className="lp2-nav">
          <a className="lp2-brand" href="/landing">
            <img src="/branding/agentcolab-logo.svg" alt="AgentColab" />
          </a>
          <div className="lp2-nav-links">
            <a href="/landing">Home</a>
            <a href="/user-guide">User Guide</a>
            <a href="/terms">Terms</a>
            <a href="/privacy">Privacy</a>
          </div>
        </nav>
      </header>

      <div className="ug-layout">
        <nav className="ug-sidebar">
          <div className="ug-sidebar-title">Contents</div>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              className={`ug-sidebar-link ${activeSection === s.id ? "ug-sidebar-link--active" : ""}`}
              onClick={() => scrollTo(s.id)}
            >
              {s.label}
            </button>
          ))}
        </nav>

        <main className="ug-content">
          <div className="ug-title-block">
            <h1>AgentColab User Guide</h1>
            <p>Everything you need to create, configure, and operate AI agents on your own infrastructure.</p>
          </div>

          <div className="ug-alert">
            <strong>Before you begin:</strong> The Home Assistant is created without a model assigned. After logging in, go to <strong>Agents</strong> in the sidebar, select the Home Assistant, and assign a model (e.g. <code>gpt-4o</code> or <code>claude-sonnet-4-20250514</code>). The assistant cannot respond to chats until a model is set.
          </div>

          {/* ================================================================
              SECTION 1: GETTING STARTED
              ================================================================ */}
          <section id="getting-started" className="ug-section">
            <h2>Getting Started</h2>

            <h3>System Requirements</h3>
            <ul>
              <li><strong>OS:</strong> Linux (Ubuntu 20.04+ / Debian 11+), macOS 12+, or Windows 10/11</li>
              <li><strong>Python:</strong> 3.11 or later</li>
              <li><strong>Node.js:</strong> 18.x LTS or later</li>
              <li><strong>Hardware:</strong> 2+ CPU cores, 4 GB RAM, 15 GB disk</li>
              <li><strong>Linux only:</strong> systemd for service management</li>
            </ul>

            <h3>Installation</h3>

            <Step n={1} title="Download the installer">
              <p>Log in at <a href="https://agentcolab.io" target="_blank" rel="noopener noreferrer">agentcolab.io</a>, go to <strong>Installers</strong>, and download the installer for your platform.</p>
            </Step>

            <Step n={2} title="Run the installer">
              <p>Choose your platform:</p>
              <div className="ug-tabs">
                <div className="ug-code-block">
                  <span>chmod +x install-*.sh && ./install-*.sh</span>
                </div>
                <p className="ug-note"><strong>Linux</strong> — Installs prerequisites (Python, Node, nginx), creates a systemd service, and configures nginx. The service starts automatically and runs on boot.</p>
              </div>
              <div className="ug-tabs">
                <div className="ug-code-block">
                  <span>chmod +x install-*.sh && ./install-*.sh</span>
                </div>
                <p className="ug-note"><strong>macOS</strong> — Installs Python via Homebrew and Node.js via nvm. Prints a manual start command at the end.</p>
              </div>
              <div className="ug-tabs">
                <div className="ug-code-block">
                  <span>python installer.py --gui</span>
                </div>
                <p className="ug-note"><strong>Windows (GUI)</strong> — Double-click the downloaded file or run with <code>--gui</code>. A dark-themed interface lets you choose access mode, set the default model, and enter your API key.</p>
              </div>
              <div className="ug-tabs">
                <div className="ug-code-block">
                  <span>python installer.py</span>
                </div>
                <p className="ug-note"><strong>Windows (CLI)</strong> — Command-line installer with <code>--token</code>, <code>--model</code>, and <code>--non-interactive</code> flags.</p>
              </div>
            </Step>

            <Step n={3} title="Open the dashboard">
              <p>The installer prints your dashboard URL when it finishes:</p>
              <ul>
                <li><strong>Linux:</strong> <code>http://&lt;lan-ip&gt;/app</code> (port 80 via nginx)</li>
                <li><strong>macOS / Windows:</strong> <code>http://&lt;lan-ip&gt;:8000/app</code></li>
              </ul>
            </Step>

            <Step n={4} title="Create your admin account">
              <p>On first load, you are prompted to create an admin account. Enter your email and choose a password. This becomes the primary administrator for the platform.</p>
            </Step>

            <Step n={5} title="Configure your API key">
              <p>To enable AI agent responses, set your <code>OPENROUTER_API_KEY</code> in the <code>.env</code> file inside the install directory. You can also provide it during install via the <code>--token</code> flag or the GUI.</p>
            </Step>

            <p>The installer handles the following automatically:</p>
            <ol>
              <li>Checks for prerequisites (Python 3.11+, Node.js 18+) and installs missing ones</li>
              <li>Downloads the platform payload with SHA-256 integrity verification</li>
              <li>Creates a Python virtual environment and installs dependencies</li>
              <li>Builds the React dashboard and configures it to be served at <code>/app</code></li>
              <li>Auto-generates secrets (JWT key, encryption key) and creates your <code>.env</code> file</li>
              <li>Registers and starts the systemd service (Linux) or auto-start on login (Windows)</li>
              <li>Runs a health check to verify everything is working</li>
            </ol>

            <h3>First Login</h3>
            <Step n={1} title="Open the dashboard">
              <p>Open the URL shown at the end of the install in your browser. You will see the login page.</p>
            </Step>
            <Step n={2} title="Create your account">
              <p>On first visit, create your admin account by entering your email and a password. This is the primary administrator for the platform.</p>
            </Step>
            <Step n={3} title="Explore the dashboard">
              <p>After logging in, you land on the main dashboard showing agent status, channel health, and recent activity.</p>
            </Step>

            <GuideImage src="01-landing-page.png" alt="AgentColab landing page" />
          </section>

          {/* ================================================================
              SECTION 2: DASHBOARD
              ================================================================ */}
          <section id="dashboard" className="ug-section">
            <h2>Dashboard</h2>
            <p>The dashboard is your central control plane. It shows four key panels that auto-refresh every 60 seconds:</p>

            <div className="ug-grid ug-grid--2">
              <div className="ug-card">
                <h4>Agent Summary</h4>
                <p>Total agents, active vs inactive, and quick access to agent management.</p>
              </div>
              <div className="ug-card">
                <h4>Channel Health</h4>
                <p>Status of all connected messaging channels (Telegram, Slack, Discord, WhatsApp) with connection indicators.</p>
              </div>
              <div className="ug-card">
                <h4>Usage & Cost</h4>
                <p>Token consumption and estimated cost breakdown across all agents and providers.</p>
              </div>
              <div className="ug-card">
                <h4>Recent Activity</h4>
                <p>Latest agent runs, tool executions, and system events in a live feed.</p>
              </div>
            </div>

            <p>The Home Assistant chat at the bottom of the dashboard is a built-in system agent that can answer questions about your platform, diagnose issues, and perform management tasks directly.</p>

            <GuideImage src="02-dashboard.png" alt="Main dashboard with four panels" />
          </section>

          {/* ================================================================
              SECTION 3: AGENTS
              ================================================================ */}
          <section id="agents" className="ug-section">
            <h2>Agents</h2>

            <h3>Creating an Agent</h3>
            <p>Agents are the core of AgentColab. Each agent has a role, a model, a set of tools, and optional knowledge. The creation wizard walks you through four steps:</p>

            <Step n={1} title="Choose a role template">
              <p>Select from 6 pre-built templates or start from scratch:</p>
              <ul>
                <li><strong>Ultimate Assistant</strong> — General-purpose agent with full tool access</li>
                <li><strong>Linux Sysadmin</strong> — Shell commands, service management, hardware audit</li>
                <li><strong>Windows Sysadmin</strong> — Remote commands via WinRM, Active Directory tools</li>
                <li><strong>Cisco Network Engineer</strong> — Network diagnostics, DNS management</li>
                <li><strong>Full-Stack Developer</strong> — Code generation, software development tools</li>
                <li><strong>Research Analyst</strong> — Web search, web crawl, document tools</li>
              </ul>
            </Step>

            <Step n={2} title="Describe your agent">
              <p>Give your agent a name and description. You can use the AI-assist button to generate suggestions based on the role you chose.</p>
            </Step>

            <Step n={3} title="Choose a model">
              <p>Pick from 355+ models. You can browse by provider, or set the model to <code>auto</code> and let the platform select the best model based on task complexity. Models are organized by tier:</p>
              <ul>
                <li><strong>Fast:</strong> Low-latency models for simple queries</li>
                <li><strong>Balanced:</strong> Mid-range models for most tasks</li>
                <li><strong>Strong:</strong> Most capable models for complex reasoning</li>
              </ul>
            </Step>

            <Step n={4} title="Review and create">
              <p>Verify your agent configuration, review the assigned tools, and create the agent. You can modify tools, model, and settings at any time after creation.</p>
            </Step>

            <GuideImage src="04-create-agent.png" alt="Agent creation wizard" />

            <h3>Managing Agents</h3>
            <p>The Agents page lists all your agents with their current status, model, and health. From here you can:</p>
            <ul>
              <li><strong>Update model</strong> — Switch models or change routing mode (direct, OpenRouter, local)</li>
              <li><strong>Manage tools</strong> — Add or remove tools from the agent's allowed set</li>
              <li><strong>Set job title</strong> — Give the agent a specific job function that shapes its behavior</li>
              <li><strong>Configure access</strong> — Control internet access, crawling, root commands, and network tools</li>
              <li><strong>Bind credentials</strong> — Attach specific vault credentials to the agent</li>
              <li><strong>Check health</strong> — Verify the agent's model route is reachable and credentials are valid</li>
              <li><strong>Delete agent</strong> — Permanently remove an agent and its configuration</li>
            </ul>

            <GuideImage src="03-agents-list.png" alt="Agent management page" />

            <h3>Running an Agent</h3>
            <p>The Run Agent page is a chat interface where you interact with any agent in real time:</p>
            <ol>
              <li>Select an agent from the dropdown at the top</li>
              <li>Type your message in the input field at the bottom</li>
              <li>The agent processes your request, calls tools as needed, and streams the response</li>
              <li>Tool executions appear in a panel below the response</li>
              <li>Conversation history is saved per agent and persists across sessions</li>
            </ol>
            <p>You can stop a running request mid-stream with the Stop button. Responses can be copied to clipboard with one click.</p>

            <GuideImage src="05-run-agent.png" alt="Agent chat interface" />

            <h3>Auto Model Selection</h3>
            <p>When an agent's model is set to <code>auto</code>, the platform evaluates each request using a 5-signal complexity scorer:</p>
            <ul>
              <li>Message length — longer messages get higher complexity</li>
              <li>Estimated tool count — more tools means more complexity</li>
              <li>Conversation depth — deeper history adds context cost</li>
              <li>Multi-step keywords — words like "then", "after", "also" signal multi-step tasks</li>
              <li>Numbered subtasks — numbered lists indicate structured work</li>
            </ul>
            <p>Based on the score and task category (coding, infrastructure, reasoning, general), the router picks the optimal model from your configured defaults.</p>
          </section>

          {/* ================================================================
              SECTION 4: CHANNEL INTEGRATIONS
              ================================================================ */}
          <section id="channels" className="ug-section">
            <h2>Channel Integrations</h2>
            <p>AgentColab supports 4 messaging channels out of the box. Each channel lets you deploy agents directly into the tools your team already uses.</p>

            <GuideImage src="06-integrations.png" alt="Integrations overview" />

            <h3>Telegram</h3>
            <Step n={1} title="Create a Telegram Bot">
              <p>Open Telegram, search for <code>@BotFather</code>, and create a new bot. Copy the bot token.</p>
            </Step>
            <Step n={2} title="Add the bot in AgentColab">
              <p>Go to Integrations &rarr; Telegram. Click "Add Bot". Paste the token. AgentColab validates it and registers a webhook automatically.</p>
            </Step>
            <Step n={3} title="Bind an agent to the bot">
              <p>Select which agent should respond to messages sent to this bot. You can bind different agents to different bots.</p>
            </Step>
            <Step n={4} title="Test">
              <p>Send a message to your Telegram bot. The agent responds directly in the chat.</p>
            </Step>
            <p>Telegram supports HTML formatting, automatic message chunking for long responses, and thread-aware conversations.</p>

            <h3>Discord</h3>
            <Step n={1} title="Create a Discord Application">
              <p>Go to the Discord Developer Portal. Create an application, add a Bot user, and copy the bot token. Enable the Message Content Intent.</p>
            </Step>
            <Step n={2} title="Invite the bot to your server">
              <p>Generate an OAuth2 invite URL with <code>bot</code> scope and appropriate permissions. Open the URL to add the bot to your Discord server.</p>
            </Step>
            <Step n={3} title="Configure in AgentColab">
              <p>Go to Integrations &rarr; Discord. Enter the bot token, server ID, and the channel ID where the bot should listen. Select which agent to bind.</p>
            </Step>
            <Step n={4} title="Test">
              <p>Mention the bot or send a message in the configured channel. The agent responds in Discord with proper markdown formatting.</p>
            </Step>

            <h3>Slack</h3>
            <Step n={1} title="Create a Slack App">
              <p>Visit api.slack.com/apps. Create a new app from scratch. Enable Event Subscriptions and configure the request URL to point to your AgentColab server's webhook endpoint.</p>
            </Step>
            <Step n={2} title="Configure bot scopes">
              <p>Add the following bot token scopes: <code>chat:write</code>, <code>channels:read</code>, <code>groups:read</code>, <code>im:history</code>, <code>channels:history</code>. Install the app to your workspace and copy the Bot User OAuth Token.</p>
            </Step>
            <Step n={3} title="Add in AgentColab">
              <p>Go to Integrations &rarr; Slack. Enter the bot token and signing secret. AgentColab validates the connection and discovers available channels.</p>
            </Step>
            <Step n={4} title="Bind and test">
              <p>Select a channel and bind an agent. Mention the bot in the Slack channel to test.</p>
            </Step>
            <p>Slack integration includes HMAC signature verification for webhook security, thread-aware replies, and automatic channel discovery.</p>

            <h3>WhatsApp (via Twilio)</h3>
            <Step n={1} title="Set up Twilio WhatsApp">
              <p>Create a Twilio account. Activate the WhatsApp sandbox or set up a WhatsApp Business API account. Copy your Account SID, Auth Token, and Phone Number.</p>
            </Step>
            <Step n={2} title="Configure webhook">
              <p>Set the Twilio WhatsApp webhook URL to point to your AgentColab server's WhatsApp endpoint.</p>
            </Step>
            <Step n={3} title="Add in AgentColab">
              <p>Go to Integrations &rarr; WhatsApp. Enter your Twilio credentials. Test the connection. Bind an agent to the phone number.</p>
            </Step>
          </section>

          {/* ================================================================
              SECTION 5: KNOWLEDGE & SKILLS
              ================================================================ */}
          <section id="skills" className="ug-section">
            <h2>Knowledge & Skills</h2>
            <p>Knowledge packs let you ground agents in your organization's expertise. Instead of relying solely on the model's training data, agents can reference your documents, notes, and trusted links.</p>

            <GuideImage src="07-skills.png" alt="Skills and knowledge management" />

            <h3>Creating a Knowledge Pack</h3>
            <Step n={1} title="Go to Skills">
              <p>Navigate to the Skills page from the sidebar.</p>
            </Step>
            <Step n={2} title="Create a new skill">
              <p>Click "Create Skill". Give it a name and description. Skills are containers for related knowledge documents.</p>
            </Step>
            <Step n={3} title="Add documents">
              <p>Import content in three ways:</p>
              <ul>
                <li><strong>Text</strong> — Paste content directly</li>
                <li><strong>File</strong> — Upload text files, markdown, or documents</li>
                <li><strong>URL</strong> — AgentColab will fetch and extract text from any web page</li>
              </ul>
            </Step>
            <Step n={4} title="Assign to an agent">
              <p>Go to the agent's settings and add the skill. The agent will now use the knowledge pack as context when responding.</p>
            </Step>

            <h3>Pre-Built Skill Packs</h3>
            <p>AgentColab includes 9 curated skill packs for common operations:</p>
            <ul>
              <li>Linux Operations and Recovery</li>
              <li>Windows Domain Operations</li>
              <li>Container Platform Operations</li>
              <li>Cisco Network Engineering</li>
              <li>Juniper/Aruba Network Operations</li>
              <li>Business Travel Operations</li>
              <li>Travel Booking and Risk Controls</li>
              <li>Travel Intake and Itinerary Design</li>
              <li>macOS Administration</li>
            </ul>
            <p>Install skill packs from the Skills page with one click. They appear as regular skills that you can customize or extend.</p>

            <h3>Semantic Search</h3>
            <p>Knowledge packs support semantic search. When an agent receives a question, it searches all assigned knowledge packs for relevant content using vector similarity. This works with OpenAI embeddings, local Ollama embeddings, or a built-in hash-based fallback.</p>

            <h3>Skill Commands</h3>
            <p>Users can invoke specific skills using slash commands in the chat:</p>
            <ul>
              <li><code>/search &lt;query&gt;</code> — Search across all knowledge packs</li>
              <li><code>/code-review</code> — Activate the code review skill</li>
              <li><code>/diagnose</code> — Run diagnostic analysis</li>
            </ul>
          </section>

          {/* ================================================================
              SECTION 6: MULTI-AGENT COLLABORATION
              ================================================================ */}
          <section id="collaboration" className="ug-section">
            <h2>Multi-Agent Collaboration</h2>
            <p>AgentColab lets multiple agents work together on complex tasks. The orchestrator coordinates work between specialized agents, tracks progress through stages, and handles agent handoffs.</p>

            <GuideImage src="09-collaboration.png" alt="Multi-agent collaboration" />

            <h3>Starting a Collaboration</h3>
            <Step n={1} title="Describe the task">
              <p>From the Collaboration page, describe what you need done. For example: "Research the latest Kubernetes security best practices, create a summary report, and generate a PDF."</p>
            </Step>
            <Step n={2} title="Choose agents">
              <p>Select which agents participate. Each agent brings its own model, tools, and knowledge. Typical roles include:</p>
              <ul>
                <li><strong>Researcher</strong> — Gathers information using web search and crawl</li>
                <li><strong>Analyst</strong> — Processes and summarizes findings</li>
                <li><strong>Writer</strong> — Formats output and generates documents</li>
                <li><strong>Operator</strong> — Executes commands or performs system tasks</li>
              </ul>
            </Step>
            <Step n={3} title="Choose execution mode">
              <p>Two modes are available:</p>
              <ul>
                <li><strong>Sequential</strong> — Agents run one after another, each receiving the previous agent's output</li>
                <li><strong>Parallel</strong> — Independent agents run simultaneously for faster results</li>
              </ul>
            </Step>
            <Step n={4} title="Monitor progress">
              <p>Stage-based progress cards show each agent's status: pending, running, completed, or failed. You can reply to agent questions in real time if an agent needs clarification.</p>
            </Step>
          </section>

          {/* ================================================================
              SECTION 7: CREDENTIAL VAULT
              ================================================================ */}
          <section id="credentials" className="ug-section">
            <h2>Credential Vault</h2>
            <p>The credential vault stores sensitive information (SSH keys, API tokens, database passwords) using Fernet symmetric encryption. Credentials never appear in plaintext in the database.</p>

            <GuideImage src="08-credential-vault.png" alt="Credential vault" />

            <h3>Supported Credential Types</h3>
            <div className="ug-grid ug-grid--3">
              <div className="ug-card">
                <h4>SSH</h4>
                <p>Username, key/path, port, host</p>
              </div>
              <div className="ug-card">
                <h4>WinRM</h4>
                <p>Username, password, port, host</p>
              </div>
              <div className="ug-card">
                <h4>Domain Admin</h4>
                <p>Username, password, domain</p>
              </div>
              <div className="ug-card">
                <h4>Cloud IAM</h4>
                <p>Access key, secret key, region</p>
              </div>
              <div className="ug-card">
                <h4>Database</h4>
                <p>Username, password, host, port, database</p>
              </div>
              <div className="ug-card">
                <h4>API Key</h4>
                <p>Key name and value</p>
              </div>
            </div>

            <h3>Adding a Credential</h3>
            <Step n={1} title="Navigate to Credential Vault">
              <p>Go to the Credential Vault page from the sidebar.</p>
            </Step>
            <Step n={2} title="Add new credential">
              <p>Click "Add Credential". Select the type, fill in the required fields, and save. The credential is encrypted immediately and stored as a Fernet ciphertext.</p>
            </Step>
            <Step n={3} title="Bind to an agent">
              <p>Go to the agent's settings, find the Credentials section, and bind the credential. When the agent needs the credential (e.g., to SSH into a server), it's decrypted at runtime and injected into the tool's environment.</p>
            </Step>

            <h3>Credential Health</h3>
            <p>The credential health dashboard shows which credentials are valid, expired, or untested. It also tracks which agents depend on each credential so you can assess the blast radius before rotating a key.</p>

            <h3>Security Notes</h3>
            <ul>
              <li>The Fernet encryption key is auto-generated on first boot and stored in <code>.env</code></li>
              <li>Credentials are decrypted only at tool execution time, never at rest</li>
              <li>All credential access is logged to the audit log</li>
              <li>Plaintext secrets in the database are automatically migrated to <code>env:</code> references on startup</li>
            </ul>
          </section>

          {/* ================================================================
              SECTION 8: GOVERNANCE & APPROVALS
              ================================================================ */}
          <section id="governance" className="ug-section">
            <h2>Governance & Approvals</h2>
            <p>AgentColab uses a 4-class risk taxonomy to govern tool execution. This ensures that destructive or critical operations always require human approval before agents can act.</p>

            <GuideImage src="11-approvals.png" alt="Approval queue" />

            <h3>Risk Classes</h3>
            <div className="ug-grid ug-grid--2">
              <div className="ug-card">
                <h4>Safe</h4>
                <p>Read-only operations: web search, weather, hardware audit, PDF generation. Execute immediately with no approval needed.</p>
              </div>
              <div className="ug-card">
                <h4>Moderate</h4>
                <p>Write operations with controlled scope: run commands, manage services, Docker control. Execute immediately but write an audit log entry.</p>
              </div>
              <div className="ug-card ug-card--warning">
                <h4>Destructive</h4>
                <p>Operations that change infrastructure state: manage VMs, manage DNS, manage AD users/groups. Require explicit approval.</p>
              </div>
              <div className="ug-card ug-card--danger">
                <h4>Critical</h4>
                <p>Irreversible operations with wide blast radius: delete VMs, force restart services, modify security policies. Always require approval with no override.</p>
              </div>
            </div>

            <h3>Approval Workflow</h3>
            <Step n={1} title="Agent requests a tool">
              <p>When an agent calls a destructive or critical tool, the request is paused and added to the approval queue.</p>
            </Step>
            <Step n={2} title="Review the request">
              <p>The Approvals page shows the pending request with: the agent name, tool name, arguments, risk class, and target context.</p>
            </Step>
            <Step n={3} title="Approve or deny">
              <p>Click Approve to let the tool execute, or Deny to reject it. The agent receives the result and continues.</p>
            </Step>

            <h3>Access Controls</h3>
            <p>Each agent has four access control flags derived from its tool set:</p>
            <ul>
              <li><strong>Internet</strong> — Can access web search, web fetch, web crawl</li>
              <li><strong>Crawl</strong> — Can crawl multiple pages from a website</li>
              <li><strong>Root</strong> — Can execute commands with elevated privileges</li>
              <li><strong>Network</strong> — Can run network diagnostics and configuration tools</li>
            </ul>
          </section>

          {/* ================================================================
              SECTION 9: API KEYS & PROVIDERS
              ================================================================ */}
          <section id="api-keys" className="ug-section">
            <h2>API Keys & Providers</h2>
            <p>AgentColab routes agent requests through 20+ AI providers. You manage provider API keys at two levels: global and per-agent.</p>

            <GuideImage src="12-api-keys.png" alt="API key management" />

            <h3>Supported Providers</h3>
            <p>The platform supports 20 providers through 6 adapters:</p>
            <ul>
              <li><strong>OpenAI adapter:</strong> OpenAI, z-ai</li>
              <li><strong>Anthropic adapter:</strong> Anthropic</li>
              <li><strong>Google adapter:</strong> Google</li>
              <li><strong>OpenRouter adapter:</strong> OpenRouter (gateway to 355+ models)</li>
              <li><strong>OpenAI-Compatible adapter:</strong> xAI, DeepSeek, Alibaba, Mistral, Groq, Together, Cerebras, SambaNova, Perplexity, MiniMax, Moonshot, Volcengine, Xiaomi</li>
              <li><strong>Local adapter:</strong> Ollama, vLLM, SGLang</li>
            </ul>

            <h3>Adding Provider Keys</h3>
            <Step n={1} title="Navigate to API Keys">
              <p>Go to the API Keys page from the sidebar.</p>
            </Step>
            <Step n={2} title="Add a provider key">
              <p>Click "Add Key". Select the provider and paste your API key. Keys are stored as environment variable references (<code>env:VAR_NAME</code>) and never appear in plaintext.</p>
            </Step>
            <Step n={3} title="Test the connection">
              <p>After adding a key, you can test it by creating an agent that uses that provider or by checking the model catalog.</p>
            </Step>

            <h3>Per-Agent Keys</h3>
            <p>You can override the global provider key for specific agents. This is useful when different agents need different accounts or billing. Configure per-agent keys from the agent's settings page.</p>

            <h3>Routing Modes</h3>
            <ul>
              <li><strong>Direct</strong> — Call the provider's API directly (fastest, lowest latency)</li>
              <li><strong>OpenRouter</strong> — Route through OpenRouter for unified access to 355+ models</li>
              <li><strong>OpenAI-Compatible</strong> — Use any OpenAI-compatible endpoint with a custom base URL</li>
              <li><strong>Local</strong> — Connect to Ollama, vLLM, or SGLang running on your infrastructure</li>
            </ul>
          </section>

          {/* ================================================================
              SECTION 10: OBSERVABILITY
              ================================================================ */}
          <section id="observability" className="ug-section">
            <h2>Observability</h2>
            <p>Monitor your agents, track costs, and keep an eye on system health from the observability suite.</p>

            <GuideImage src="10-usage.png" alt="Usage and cost tracking" />

            <h3>Usage & Cost Tracking</h3>
            <p>The Usage page shows token consumption and estimated costs for every agent run. Break down by agent, user, model, or time period. Costs are estimated based on model pricing data from the catalog.</p>

            <h3>Activity Stream</h3>
            <p>A real-time feed of everything happening on the platform: agent runs, tool executions, channel messages, approval decisions, and system events. Filter by type, agent, or time range.</p>

            <h3>System Metrics</h3>
            <p>System-level metrics including CPU usage, memory consumption, disk space, and service health. The metrics dashboard auto-refreshes to give you a live view.</p>

            <GuideImage src="13-metrics.png" alt="System metrics" />

            <h3>Health Checks</h3>
            <p>The platform continuously monitors agent route health. If a model provider becomes unreachable or an API key expires, the health check detects it and reports the status on the dashboard. Check any agent's health from the agents list.</p>

            <h3>Observability Dashboard</h3>
            <p>The Observability page provides a consolidated view of all system telemetry, route health probes, and diagnostic data. Use it for troubleshooting and performance analysis.</p>
          </section>

          {/* ================================================================
              SECTION 11: ADMINISTRATION
              ================================================================ */}
          <section id="administration" className="ug-section">
            <h2>Administration</h2>

            <h3>User Management</h3>
            <p>Manage users from the Users page. Three roles are available:</p>
            <ul>
              <li><strong>Admin</strong> — Full access to all features, user management, system settings</li>
              <li><strong>Operator</strong> — Agent management, tool usage, channel configuration</li>
              <li><strong>Viewer</strong> — Read-only access to dashboards and activity</li>
            </ul>
            <p>Invite new users by email. Each user gets a unique JWT token for authentication with 24-hour expiry.</p>

            <h3>Team Management</h3>
            <p>Organize users into teams. Teams can be assigned agents and credentials collectively, making it easy to manage permissions at scale.</p>

            <h3>Backup & Restore</h3>
            <Step n={1} title="Create a backup">
              <p>Go to System &rarr; Backup. Click "Export". This creates a complete snapshot of your database, including all agents, configurations, credentials, and conversation history.</p>
            </Step>
            <Step n={2} title="Download the backup file">
              <p>The backup is a JSON file that you can download and store securely.</p>
            </Step>
            <Step n={3} title="Restore from backup">
              <p>Go to System &rarr; Backup. Click "Import" and upload your backup file. The platform restores all data from the snapshot.</p>
            </Step>
            <p>You can also enable automatic daily backups by setting <code>AUTO_BACKUP_ENABLED=1</code> in your <code>.env</code> file.</p>

            <h3>Environment Variables</h3>
            <p>Key configuration options in your <code>.env</code> file:</p>
            <div className="ug-table-wrap">
              <table className="ug-table">
                <thead>
                  <tr><th>Variable</th><th>Purpose</th><th>Default</th></tr>
                </thead>
                <tbody>
                  <tr><td><code>JWT_SECRET_KEY</code></td><td>Token signing key (auto-generated)</td><td>Auto</td></tr>
                  <tr><td><code>OPENROUTER_API_KEY</code></td><td>OpenRouter API key for 355+ models</td><td>&mdash;</td></tr>
                  <tr><td><code>OPENAI_API_KEY</code></td><td>OpenAI API key</td><td>&mdash;</td></tr>
                  <tr><td><code>TELEGRAM_BOT_TOKEN</code></td><td>Default Telegram bot token</td><td>&mdash;</td></tr>
                  <tr><td><code>ALLOW_SUDO_TOOLS</code></td><td>Allow agents to run sudo commands</td><td>false</td></tr>
                  <tr><td><code>RUN_COMMAND_ALLOW_ALL</code></td><td>Disable command safe-listing</td><td>false</td></tr>
                  <tr><td><code>RATE_LIMIT_ENABLED</code></td><td>Enable token-bucket rate limiting</td><td>false</td></tr>
                  <tr><td><code>CIRCUIT_BREAKER_ENABLED</code></td><td>Enable per-provider circuit breaker</td><td>false</td></tr>
                  <tr><td><code>AUTO_BACKUP_ENABLED</code></td><td>Enable daily automatic backups</td><td>false</td></tr>
                  <tr><td><code>EMAIL_WORKER_ENABLED</code></td><td>Start IMAP email worker</td><td>false</td></tr>
                  <tr><td><code>EMBEDDING_PROVIDER</code></td><td>Embedding backend (openai/ollama/hash)</td><td>hash</td></tr>
                  <tr><td><code>MODEL_CATALOG_AUTO_REFRESH_ENABLED</code></td><td>Auto-refresh model catalog</td><td>false</td></tr>
                </tbody>
              </table>
            </div>

            <h3>Uninstalling</h3>
            <p>To remove AgentColab, re-run the installer with the uninstall flag:</p>

            <div className="ug-grid ug-grid--3">
              <div className="ug-card">
                <h4>Linux</h4>
                <div className="ug-code-block">
                  <span>sudo systemctl stop agentcolab<br/>sudo systemctl disable agentcolab<br/>rm -rf ~/agentcolab</span>
                </div>
              </div>
              <div className="ug-card">
                <h4>Windows</h4>
                <div className="ug-code-block">
                  <span>python installer.py --uninstall</span>
                </div>
                <p>Removes the install directory and auto-start registry entry.</p>
              </div>
              <div className="ug-card">
                <h4>macOS</h4>
                <div className="ug-code-block">
                  <span>rm -rf ~/agentcolab</span>
                </div>
                <p>Stop the uvicorn process first if running.</p>
              </div>
            </div>

            <h3>Upgrading</h3>
            <p>Re-download the latest installer from <a href="https://agentcolab.io" target="_blank" rel="noopener noreferrer">agentcolab.io</a> and run it again. The installer detects existing installations and enters upgrade mode automatically, preserving your <code>.env</code>, database, and data. A backup snapshot is created before updating.</p>

            <h3>Troubleshooting</h3>
            <ul>
              <li><strong>Linux:</strong> Check logs with <code>journalctl -u agentcolab -n 100</code></li>
              <li><strong>macOS / Windows:</strong> Run manually to see errors:
                <div className="ug-code-block" style={{marginTop: "0.5rem"}}>
                  <span>cd ~/agentcolab && source venv/bin/activate && uvicorn backend.app.main:app --host 0.0.0.0 --port 8000</span>
                </div>
              </li>
              <li><strong>Missing AI responses:</strong> Set <code>OPENROUTER_API_KEY</code> in <code>.env</code> and restart</li>
            </ul>
          </section>

          <div className="ug-footer-nav">
            <a href="/landing" className="lp2-button lp2-button--primary">Back to Home</a>
            <a href="/terms" className="lp2-button lp2-button--secondary">Terms of Service</a>
          </div>
        </main>
      </div>

      <footer className="ug-footer">
        <div className="ug-footer-inner">
          <div className="ug-footer-brand">
            <span className="ug-footer-logo">
              <img src="/branding/agentcolab-mark.svg" alt="" />
              AgentColab
            </span>
            <p>Self-hosted AI agent platform for multi-agent operations.</p>
          </div>
          <div className="ug-footer-links">
            <div className="ug-footer-col">
              <h4>Product</h4>
              <a href="/landing#quick-start">Quick Start</a>
              <a href="/landing#how-it-works">How It Works</a>
            </div>
            <div className="ug-footer-col">
              <h4>Resources</h4>
              <a href="/user-guide">User Guide</a>
              <a href="https://github.com/dipampatel1/agentcolab" target="_blank" rel="noopener noreferrer">GitHub</a>
            </div>
            <div className="ug-footer-col">
              <h4>Legal</h4>
              <a href="/terms">Terms</a>
              <a href="/privacy">Privacy</a>
            </div>
          </div>
        </div>
        <div className="ug-footer-bottom">
          <p>&copy; {new Date().getFullYear()} AgentColab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  )
}
